import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { uploadFile } from '@/utils/storage';
import { useMovimentacaoMutations } from '../../../hooks/data';
import { useToast } from '@/hooks/useToast';

interface FotosEquipamentoSectionProps {
  solicitacaoId: string;
  movimentacaoId: string;
  itemId: string;
  fotos: string[];
  podeEditar: boolean;
}

/**
 * Fotos de um equipamento específico da Movimentação (MovimentacaoItemExecucao.fotos) —
 * cada equipamento tem as suas, em vez de tudo misturado no nível da Movimentação.
 * `etapas.fotos` é recalculado pelo movimentacaoService (ver calcularEtapaFotos): só
 * conclui quando todo equipamento tem pelo menos FOTOS_MINIMAS_POR_EQUIPAMENTO fotos.
 * Sem contador "x/2" de propósito — induzia a mandar só o mínimo; o mínimo só é cobrado
 * ao tentar gerar o termo (GerarTermoButton) e na pendência da Movimentação.
 */
export default function FotosEquipamentoSection({
  solicitacaoId,
  movimentacaoId,
  itemId,
  fotos,
  podeEditar,
}: FotosEquipamentoSectionProps) {
  const toast = useToast();
  const { atualizarFotosEquipamento } = useMovimentacaoMutations(solicitacaoId);
  const [enviando, setEnviando] = useState(false);

  if (!podeEditar && fotos.length === 0) return null;

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const arquivos = Array.from(event.target.files ?? []);
    if (arquivos.length === 0) return;

    setEnviando(true);
    try {
      const novasUrls = await Promise.all(
        arquivos.map((arquivo) => {
          const path = `si_solicitacoes/${solicitacaoId}/movimentacoes/${movimentacaoId}/equipamentos/${itemId}/fotos/${crypto.randomUUID()}-${arquivo.name}`;
          return uploadFile(arquivo, path);
        })
      );

      await atualizarFotosEquipamento.mutateAsync({
        movimentacaoId,
        itemId,
        alterarFotos: (fotosAtuais) => [...fotosAtuais, ...novasUrls],
      });
      toast.success(`${novasUrls.length} foto(s) enviada(s).`);
    } catch (error: any) {
      toast.error(`Erro ao enviar foto: ${error?.message || 'erro desconhecido'}`);
    } finally {
      setEnviando(false);
      event.target.value = '';
    }
  };

  const handleRemover = (url: string) => {
    atualizarFotosEquipamento.mutate({
      movimentacaoId,
      itemId,
      alterarFotos: (fotosAtuais) => fotosAtuais.filter((foto) => foto !== url),
    });
  };

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Fotos do equipamento</p>

      {fotos.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {fotos.map((url) => (
            <div key={url} className="relative">
              <a href={url} target="_blank" rel="noreferrer">
                <img src={url} alt="Foto do equipamento" className="h-16 w-16 rounded-md object-cover" />
              </a>
              {podeEditar && (
                <button
                  type="button"
                  className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 text-gray-500 shadow hover:text-red-600"
                  onClick={() => handleRemover(url)}
                  title="Remover foto"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {podeEditar && (
        <label className="mt-2 inline-block cursor-pointer text-sm font-semibold text-primary-700 hover:text-primary-800">
          {enviando ? 'Enviando...' : '+ Anexar fotos'}
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            disabled={enviando}
            onChange={handleUpload}
          />
        </label>
      )}
    </div>
  );
}
